import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import { ReactComponent as CompletionAgeIcon } from 'assets/wg_completion_age.svg';
import WgChip from 'pages/WellGuardian/WgChip';
import { getChipConfigFromWellOnlineStatus } from 'pages/WellGuardian/WgChip/constants/chipConfiguration';
import { useFetchWgHeaderData } from 'pages/WellGuardian/hooks/useFetchWgHeaderData';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useCountryState, useSelectedAsset, useSelectedSite } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WellInfoForSite } from 'types/wellGuardian';

import styles from './WellHealthHeader.module.scss';
import SelectWell from '../SelectWell';
import SiteSelector from '../SiteSelector';

const WellHealthHeader: React.FunctionComponent = () => {
  const intl = useIntl();
  const asset = useSelectedAsset();
  const site = useSelectedSite();
  const countryState = useCountryState();

  const { tuwi } = useParams<{ tuwi: string }>();
  const { wellInfoLoading, wellInfo } = useFetchWgHeaderData(tuwi, intl);
  const [wellsForSiteLoading, setWellsForSiteLoading] = useState<boolean>(false);
  const [wellsForSite, setWellsForSite] = useState<WellInfoForSite[]>([]);

  const fetchWellsForSite = useCallback(async () => {
    setWellsForSiteLoading(true);
    const wellsResponse = await wellGuardianApiClient.getWellsInfoForSite(
      asset,
      site,
      countryState,
    );
    const wellsForSelectedSite = wellsResponse.map(item => ({
      wellName: item.wellName,
      wellViewId: item.wellViewId,
    }));
    setWellsForSite(wellsForSelectedSite);
    setWellsForSiteLoading(false);
  }, [asset, site, countryState]);

  useEffect(() => {
    fetchWellsForSite();
  }, [fetchWellsForSite]);

  if (wellInfoLoading) {
    return (
      <Grid item className={styles.wellInfoLoading}>
        <LinearProgress classes={{ barColorPrimary: styles.barColor }} />
      </Grid>
    );
  }

  return (
    <>
      <Grid item className={styles.wellName}>
        <div className={styles.siteSelectorContainer}>
          <SiteSelector variant="dark" color={true} />
        </div>
      </Grid>
      <Grid item className={styles.wellName}>
        <SelectWell
          wellsForSite={wellsForSite}
          wellsForSiteLoading={wellsForSiteLoading}
          wellInfo={wellInfo}
        />
      </Grid>
      <Grid
        container
        item
        className={styles.wellInfoContainer}
        data-testid="wg-header-well-type-grid"
      >
        <Grid item>
          <span>Well Status: </span>
          {wellInfo?.wellOnlineStatus === null
            ? 'No PI tag'
            : wellInfo?.wellOnlineStatus && (
                <WgChip
                  variant="default"
                  config={getChipConfigFromWellOnlineStatus(wellInfo?.wellOnlineStatus)}
                />
              )}
        </Grid>
        <Grid item>
          <WgChip
            variant="rounded"
            config={{
              text: `${wellInfo?.slot} | ${wellInfo?.wellType}`,
              className: styles.wellType,
            }}
          />
        </Grid>
        <Grid item data-testid="wg-header-well-status-grid">
          <WgChip
            variant="rounded"
            config={{ text: wellInfo?.lifeCycleAliasDesc, className: styles.wellStatus }}
          />
        </Grid>
        <Grid item data-testid="wg-header-completion-age-grid">
          <WgChip
            variant="rounded"
            config={{
              text: intl.formatMessage(
                {
                  id: 'wellGuardian.header.completionAge',
                },
                {
                  age:
                    wellInfo && wellInfo.completionAge
                      ? Math.round(Number(wellInfo.completionAge))
                      : 'N/A',
                },
              ),
              startIcon: <CompletionAgeIcon />,
              className: styles.wellCompletionAge,
            }}
          />
        </Grid>
        <Grid item data-testid="wg-header-well-status-grid">
          <WgChip
            variant="rounded"
            config={{
              text: intl.formatMessage(
                {
                  id: 'wellGuardian.header.eruptivity',
                },
                {
                  eruptivity: wellInfo && wellInfo.eruptivity,
                },
              ),
              className: styles.wellCompletionAge,
            }}
          />
        </Grid>
        <Grid item data-testid="wg-header-well-status-grid">
          <WgChip
            variant="rounded"
            config={{
              text: intl.formatMessage(
                {
                  id: 'wellGuardian.header.location',
                },
                {
                  location: wellInfo && wellInfo.location,
                },
              ),
              className: styles.wellCompletionAge,
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default WellHealthHeader;
